import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';

import { selectedSubscriptionTypeAtom } from '@/atoms/subscription';

import { usePricingOptions } from '../api/usePricingOptions';
import { useSubscriptionType } from '../auth';
import useBillingMode from '../billing/useBillingMode';
import { useSettings } from '../state/useSettings';

import type { PricingByCurrency } from '../api/usePricingOptions';
import type { Plan } from '@/fixtures/plans';

const useConversionDetails = (): {
  subscriptionLength: 'Annual' | 'Monthly';
  subscriptionType: Plan['name'];
  asPath: string;
  currencyCode: string;
  value: number;
  paymentType: 'card' | 'paypal';
} => {
  const { settings } = useSettings();
  const { selectedBillingMode } = useBillingMode();
  const router = useRouter();
  const selectedSubscriptionType = useAtomValue(selectedSubscriptionTypeAtom);
  const currentSubscriptionType = useSubscriptionType();
  const { data: pricingOptions } = usePricingOptions();

  const billingMode = selectedBillingMode ?? 'monthly';
  const isAnnual = billingMode === 'yearly';

  const paymentType = settings?.subscriptionData?.paid_through_paypal
    ? 'paypal'
    : 'card';

  const subscriptionLength = isAnnual ? 'Annual' : 'Monthly';

  const subscriptionType = selectedSubscriptionType ?? currentSubscriptionType;
  const currencyCode = settings?.currency.code ?? 'GBP';

  const currencyKey = currencyCode.toLowerCase() as keyof PricingByCurrency;
  const selectedPlan = pricingOptions?.find(
    (option) => option.title.toLowerCase() === subscriptionType.toLowerCase()
  );
  const value = selectedPlan?.pricing[currencyKey]
    ? isAnnual
      ? selectedPlan.pricing[currencyKey].yearly
      : selectedPlan.pricing[currencyKey].monthly
    : 0;

  return {
    subscriptionLength,
    subscriptionType,
    asPath: router.asPath,
    currencyCode,
    value,
    paymentType,
  };
};

export default useConversionDetails;
